import React, { useState, Fragment } from 'react';
import { useMutation } from 'react-apollo';
import moment from 'moment';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';
import { useHistory } from 'react-router-dom';

import getLogger from '../../../../../../util/logger';
import { rosterIdentitiesCreate as rosterIdentitiesCreateMutation } from '../../../../../../graphql/mutations/roster-identity';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { parsePhone } from '../../../../../../util/phone';
import {
  CSV_DATE_FORMAT,
  getRosterIdentityGenderEnum,
  getRosterIdentityGradeEnum,
  validateRosterIdentities,
  isValidImageUrl,
} from './helpers';
import uuidv4 from '../../../../../../util/uuid';
import getConfig from '../../../../../../util/config';
import { ALBUM_FOLDER_MEMBERS_IMPORT_MODE } from '../../../AlbumFolder/AlbumFolders/AlbumFolderMembers/components/AlbumFolderIdentityMembersCSVUploadModal/constants';
import { capitalize } from '../../../../../../util/text';
import useUniformConfigurationSelector from '../useUniformConfigurationSelector';
import useUniformSportGeneration from '../useUniformSportGeneration';
import { ALBUM_FOLDER_TAG_GENDER } from '../../../../../../constants/album-folder-tags';
import useBasename from '../../../../../../components/routing/useBasename';
import { ALERT_MODAL } from '../../../../../../constants/modals';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import Button from '../../../../../../components/global/Button';

const log = getLogger('useRosterIdentitiesCreate');
const MAX_REF_ID_MERGE_CLONES = 3;

/**
 * @typedef {Object} UseRosterIdentitiesCreateOutput
 * @property {function} onRosterIdentitiesCreate Async method that creates roster identities.
 * @property {function} onRosterIdentitiesCreateFromColumnMatchedData Async method that creates roster identities from a properly formatted spradsheet after it has been through the column matcher.
 * @property {boolean} loading Whether the hook is processing something.
 * @property {boolean} success Whether the process that was running was successful or not.
 * @property {Array<String>} [errors] Errors ocurring during the creation of the roster identities.
 * @property {Array<Object>} createdRosterIdentities List of roster identities that have been created.
 * @property {Array<ValidatedRosterIdentity>} invalidRosterIdentities Validated roster ids that presented errors upon validation.
 * @property {validateRosterIdentities} validateRosterIdentities Validates roster identities input.
 */

/**
 * Handles creation of multiple roster identities.
 *
 * @param {Object} options the available options
 * @param {Object} [options.identityCreateProps] properties passed on a per-identity based to identityCreate mutation
 *
 * @param {Boolean} options.isYearbookEvent whether or not the hook is used for a yearbook (PSPA) event
 * @param {String} options.albumFolderId the album folder id if available
 * @returns {UseRosterIdentitiesCreateOutput}
 */
const useRosterIdentitiesCreate = ({
  identityCreateProps = {},
  isYearbookEvent = false,
  albumFolderId,
} = {}) => {
  const history = useHistory();
  const closeModal = useCloseModal();
  const basename = useBasename();
  const { albumFolder, selectorOptions: validGenderSportCombinations } =
    useUniformConfigurationSelector({ albumFolderId });
  const { generateSportCombinations } = useUniformSportGeneration({
    albumFolderId,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [sportInformationMissingWarning, setSportInformationMissingWarning] =
    useState(false);
  const [rosterIdentitiesCreate] = useMutation(rosterIdentitiesCreateMutation);
  const [invalidRosterIdentities, setInvalidRosterIdentities] = useState([]);
  const [invalidDataStore, setinvalidDataStore] = useState({
    claimed: false,
    grantPurchase: false,
    uniformConfiguration: undefined,
  });
  // Invalid items are generally set based on GQL, meaning these values
  // Do not need to be removed at any point.
  const [invalidItems, setInvalidItems] = useState({
    phones: [],
    addresses: [],
    names: [],
    email_addresses: [],
    ref_ids: [],
  });
  const [createdRosterIdentities, setCreatedRosterIdentities] = useState([]);
  const config = getConfig();

  const prepareColumnData = rows => {
    const rosterIdentities = [];
    const refIdCopies = {};
    const missingGenderSportCombinations = [];
    let missingSportData = false;
    const invalidUrls = [];

    rows.forEach(row => {
      const {
        firstName,
        lastName,
        phoneNumber,
        emailAddress,
        dateOfBirth,
        organizationRefId,
        selfieFilename,
        selfiePhotoUrl,
        gender,
        sport,
        level,
        team,
        jerseyNumber,
        grade,
        className,
        teacherName,
        department,
        period,
        track,
        title,
        cabinName,
        groupName,
      } = row;
      const linkedIdentities = [];
      const linkedIdentityAddresses = [];
      const emailAddresses = (emailAddress ? (String(emailAddress) ?? '') : '')
        .split(' ')
        .filter(Boolean)
        .map(email => ({ email_address: email }));
      const selfieFilenames = (
        selfieFilename ? (String(selfieFilename) ?? '') : ''
      )
        .split(' ')
        .filter(Boolean)
        .map(filename => ({ selfie_photo_filename: filename }));
      const selfiePhotoUrls = (
        selfiePhotoUrl ? (String(selfiePhotoUrl) ?? '') : ''
      )
        .split(' ')
        .filter(Boolean)
        .filter(url => {
          if (!isValidImageUrl(url.trim())) {
            invalidUrls.push(
              `Invalid image URL "${url}". URL must end with a valid image extension (.jpg, .jpeg, .png, .heic, .heif)`,
            );
            return false;
          }
          return true;
        })
        .map(url => ({ selfie_photo_url: url }));

      if (phoneNumber || row.addressLine1) {
        const address = {
          name: `${firstName} ${lastName}`.trim(),
          address_line_1: row.addressLine1,
          address_line_2: row.addressLine2 ?? '',
          city: row.city,
          state: row.state,
          post_code: row.zipCode,
          country_id: config.country.usa.id,
        };

        const phoneNumbers = (phoneNumber?.toString() ?? '')
          .split(' ')
          .filter(Boolean);

        phoneNumbers.forEach(phone => {
          const parsedPhone = parsePhone(phone?.toString() ?? '');
          const internationalPhoneNumber =
            parsedPhone.length < 11 && !parsedPhone.startsWith('1')
              ? `1${parsedPhone}`
              : parsedPhone;

          linkedIdentities.push({
            id: internationalPhoneNumber,
            phone_number: internationalPhoneNumber,
            // Pass the address anyway, it gets validated later on
            addresses: row.addressLine1 ? [address] : undefined,
          });
        });

        if (!phoneNumbers?.length && row.addressLine1) {
          linkedIdentityAddresses.push(address);
        }
      }

      const sanitizedSport = capitalize((sport ?? '').toLowerCase().trim());
      const sanitizedGender = getRosterIdentityGenderEnum(gender);
      // We will exclude "JV" as it's own level regardless of capitalization
      const sanitizedLevel =
        (level ?? '')?.trim().toLowerCase() === 'jv'
          ? 'JV'
          : capitalize((level ?? '').toLowerCase().trim());
      const sanitizedTeam = capitalize((team ?? '').toLowerCase().trim());
      const tags = [];

      if (
        !sanitizedSport &&
        (sanitizedLevel || sanitizedTeam || jerseyNumber)
      ) {
        missingSportData = true;
      }

      // Track if this row has a missing combination that needs to be generated
      let hasMissingCombination = false;

      if (sanitizedSport && sanitizedGender) {
        const genderSportTag = validGenderSportCombinations.find(
          combo =>
            (combo.sport ?? '').toLowerCase() ===
              sanitizedSport.toLowerCase() &&
            (combo.gender ?? '').toLowerCase() ===
              sanitizedGender.toLowerCase() &&
            (combo.level ?? '').toLowerCase() ===
              sanitizedLevel.toLowerCase() &&
            (combo.team ?? '').toLowerCase() === sanitizedTeam.toLowerCase(),
        );

        if (!genderSportTag) {
          // Check if this combination is already in the missing list
          const combinationKey = `${sanitizedSport.toLowerCase()}-${sanitizedGender.toLowerCase()}-${sanitizedLevel.toLowerCase()}-${sanitizedTeam.toLowerCase()}`;
          const alreadyMissing = missingGenderSportCombinations.some(
            combo => combo.key === combinationKey,
          );

          if (!alreadyMissing) {
            missingGenderSportCombinations.push({
              sport: sanitizedSport,
              gender: sanitizedGender,
              level: sanitizedLevel,
              team: sanitizedTeam,
              key: combinationKey,
            });
          }
          hasMissingCombination = true;
        } else {
          tags.push({
            ...omit(genderSportTag, [
              'id',
              'gender',
              'sport',
              'level',
              'team',
              'label',
            ]),
            jersey_number: jerseyNumber,
          });
        }
      } else if (sanitizedSport && !sanitizedGender) {
        const sportTag = validGenderSportCombinations.find(
          combo =>
            (combo.sport ?? '').toLowerCase() ===
              sanitizedSport.toLowerCase() &&
            (combo.level ?? '').toLowerCase() ===
              sanitizedLevel.toLowerCase() &&
            (combo.team ?? '').toLowerCase() === sanitizedTeam.toLowerCase(),
        );

        if (!sportTag) {
          // Check if this combination is already in the missing list
          const combinationKey = `${sanitizedSport.toLowerCase()}--${sanitizedLevel.toLowerCase()}-${sanitizedTeam.toLowerCase()}`;
          const alreadyMissing = missingGenderSportCombinations.some(
            combo => combo.key === combinationKey,
          );

          if (!alreadyMissing) {
            missingGenderSportCombinations.push({
              sport: sanitizedSport,
              level: sanitizedLevel,
              team: sanitizedTeam,
              key: combinationKey,
            });
          }
          hasMissingCombination = true;
        } else if (sportTag && !sportTag?.gender) {
          tags.push({
            ...omit(sportTag, [
              'id',
              'gender',
              'sport',
              'level',
              'team',
              'label',
            ]),
            jersey_number: jerseyNumber,
          });
        } else {
          setSportInformationMissingWarning(true);
        }
      }

      const rosterIdentity = {
        id: uuidv4(),
        first_name: firstName ? (String(firstName)?.trim() ?? '') : '',
        last_name: lastName ? (String(lastName)?.trim() ?? '') : '',
        email_addresses: emailAddresses,
        date_of_birth:
          dateOfBirth && moment(dateOfBirth).isValid()
            ? moment.utc(dateOfBirth).format(CSV_DATE_FORMAT) // This is supposed to be a raw string - Not a moment one.
            : // Make sure we pass dateOfBirth here so that it can be validated  & errored out.
              dateOfBirth || null,
        organization_ref_id: organizationRefId,
        selfie_photo_filenames: selfieFilenames,
        selfie_photo_urls: selfiePhotoUrls,
        linked_identities: linkedIdentities,
        linked_identity_addresses: linkedIdentityAddresses,
        tags,
        gender: sanitizedGender,
        jersey_number: jerseyNumber,
        grade: getRosterIdentityGradeEnum(grade),
        class_name: className,
        teacher_name: teacherName,
        department,
        period,
        track,
        title,
        cabin_name: cabinName,
        group_name: groupName,
        // Store sport info for later tag assignment after generation
        pendingSportInfo: hasMissingCombination
          ? {
              sport: sanitizedSport,
              gender: sanitizedGender,
              level: sanitizedLevel,
              team: sanitizedTeam,
            }
          : undefined,
      };

      // If there is no org ref id there is no need for further checks.
      if (!rosterIdentity.organization_ref_id) {
        rosterIdentities.push(rosterIdentity);
        return;
      }

      const existingIdentityIdx = rosterIdentities.findIndex(
        mergedRosterIdentity =>
          mergedRosterIdentity.organization_ref_id ===
          rosterIdentity.organization_ref_id,
      );

      // If there is no existing entry with the same ref id, add this one.
      if (existingIdentityIdx === -1) {
        refIdCopies[rosterIdentity.organization_ref_id] = 1;
        rosterIdentities.push(rosterIdentity);
        return;
      }

      // If there is, add the additional linked identities & emails to this one.
      const existingRosterIdentity = rosterIdentities[existingIdentityIdx];

      // If the identity has a different name or last name, do not merge. This should
      // produce an error for the user to fix.
      if (
        existingRosterIdentity.first_name.toLowerCase().trim() !==
          rosterIdentity.first_name.toLowerCase().trim() ||
        existingRosterIdentity.last_name.toLowerCase().trim() !==
          rosterIdentity.last_name.toLowerCase().trim()
      ) {
        rosterIdentities.push(rosterIdentity);
        return;
      }

      rosterIdentities[existingIdentityIdx] = {
        ...existingRosterIdentity,
        linked_identities: uniqBy(
          [
            ...existingRosterIdentity.linked_identities,
            ...rosterIdentity.linked_identities,
          ],
          'id',
        ),
        email_addresses: uniqBy(
          [
            ...existingRosterIdentity.email_addresses,
            ...rosterIdentity.email_addresses,
          ],
          'email_address',
        ),
      };
      //   Update ref copies count
      refIdCopies[existingRosterIdentity.organization_ref_id] =
        (refIdCopies?.[existingRosterIdentity.organization_ref_id] ?? 0) + 1;
    });

    return {
      rosterIdentities,
      missingSportData,
      missingGenderSportCombinations,
      maximumRefIdCopiesExceeder: Object.keys(refIdCopies).find(
        refId => refIdCopies[refId] > MAX_REF_ID_MERGE_CLONES,
      ),
      invalidUrls,
    };
  };

  /**
   * Runs the actual create mutation. Consider this the last step of creation.
   *
   * @param {Array<Object>} rosterIdentities Array of roster identities. Each of this is expected to contain a unique ID.
   * @param {Boolean} claimed whether to set the roster identities as claimed or not
   * @param {Boolean} grantPurchase whether to grant purchases to the roster identities or not
   * @param {Object} uniformConfiguration the uniform configuration (tag, level, team) to apply to this set of roster identities
   * @param {Object} contactLinkConfiguration the contact link configuration (album folder, albums) to link this set of roster identities to
   * @param {Boolean} requiresAddress whether or not imports require address to happen
   * @param {Boolean} ignoreContactInformationErrors whether or not imports ignore lack of contact info (phone, emails) when erroring out.
   * @returns {UseRosterIdentitiesCreateOutput}
   */
  const onRosterIdentitiesCreate = async (
    rosterIdentities,
    claimed = false,
    grantPurchase = false,
    uniformConfiguration = undefined,
    contactLinkConfiguration = undefined,
    requiresAddress = false,
    ignoreContactInformationErrors = false,
  ) => {
    setSuccess(false);
    setErrors([]);

    const {
      validRosterIdentities,
      // Will be combined with the invalid ones post-create too.
      // This because we can't validate phone numbers.
      invalidRosterIdentities: invalidRosterIdentitiesPreCreate,
    } = validateRosterIdentities({
      rosterIdentities: rosterIdentities.filter(({ id }) => !!id),
      invalidItems,
      importMode: uniformConfiguration
        ? ALBUM_FOLDER_MEMBERS_IMPORT_MODE.UNIFORM
        : ALBUM_FOLDER_MEMBERS_IMPORT_MODE.GENERAL,
      requiresAddress,
      ignoreContactInformationErrors,
      isYearbookEvent,
    });

    log.info(
      'onRosterIdentitiesCreate :: valid identities (will run through create): ',
      validRosterIdentities,
      'invalid identities pre-create: ',
      invalidRosterIdentitiesPreCreate,
    );

    if (validRosterIdentities.length === 0) {
      // only invalid identities found.
      log.info(
        'onRosterIdentitiesCreate :: No valid identities found. Create process will be cancelled.',
      );
      setInvalidRosterIdentities(invalidRosterIdentitiesPreCreate);
      setinvalidDataStore({
        claimed,
        grantPurchase,
        uniformConfiguration,
        contactLinkConfiguration,
      });
      return;
    }

    setLoading(true);

    try {
      const rosterIdentitiesInput = validRosterIdentities.map(
        rosterIdentity => {
          const rosterIdentityTags = (() => {
            if (rosterIdentity.tags?.length > 0) {
              return rosterIdentity.tags;
            }

            if (uniformConfiguration) {
              return [
                {
                  ...omit(uniformConfiguration, [
                    'id',
                    'gender',
                    'sport',
                    'level',
                    'team',
                    'label',
                  ]),
                  jersey_number: rosterIdentity.jersey_number,
                },
              ];
            }

            return undefined;
          })();

          const linkedAlbumFolderIds = (() => {
            if (rosterIdentityTags?.length > 0) {
              return new Set(
                [
                  ...(identityCreateProps?.linked_album_folder_ids ?? []),
                  albumFolderId,
                  albumFolder?.id,
                ].filter(Boolean),
              );
            }

            return identityCreateProps?.linked_album_folder_ids ?? [];
          })();

          const linkedEntities = [
            ...(identityCreateProps?.linked_album_ids ?? []),
            ...(linkedAlbumFolderIds ?? []),
          ];

          const omittedRosterIdentityFields = [
            'id',
            'jersey_number',
            'sport',
            'level',
            'team',
            'tags',
            'organization_ref_id',
            'cabin_name', // cabin_name is only valid in links, never on roster identity
            'group_name', // group_name is only valid in links, never on roster identity
          ];

          // Only omit these metadata fields if we have album links to store them in
          if (linkedEntities.length > 0) {
            omittedRosterIdentityFields.push(
              'grade',
              'class_name',
              'teacher_name',
              'department',
              'period',
              'track',
              'title',
            );
          }

          return {
            ...omit(rosterIdentity, omittedRosterIdentityFields),
            linked_identities: rosterIdentity.linked_identities.map(
              linkedIdentity => ({ ...omit(linkedIdentity, ['id']) }),
            ),
            tags: rosterIdentityTags,
            contact_link_album_folder_ids:
              (contactLinkConfiguration?.albums?.length ?? 0) === 0
                ? [contactLinkConfiguration?.albumFolderId].filter(Boolean)
                : [],
            contact_link_album_ids: (
              contactLinkConfiguration?.albums ?? []
            ).map(({ id, value }) => id ?? value),
            ...(identityCreateProps ?? {}),
            links: (() => {
              const albumIds = [
                ...(identityCreateProps?.linked_album_ids ?? []),
                ...linkedAlbumFolderIds,
              ];

              // Only create links if we have album IDs (hierarchy_item_id is required)
              if (albumIds.length > 0) {
                const metadata = pick(rosterIdentity, [
                  'grade',
                  'class_name',
                  'teacher_name',
                  'department',
                  'period',
                  'track',
                  'title',
                  'cabin_name',
                  'group_name',
                ]);

                return albumIds.map(hierarchyItemId => ({
                  hierarchy_item_id: hierarchyItemId,
                  ref_id: rosterIdentity?.organization_ref_id,
                  ...metadata,
                }));
              }

              return [];
            })(),
          };
        },
      );

      const {
        data: {
          rosterIdentitiesCreate: {
            roster_identities: createdRosterIdentitiesCurrent = [],
            invalid_names: invalidNames = [],
            invalid_phone_numbers: invalidPhones = [],
            invalid_addresses: invalidAddresses = [],
            invalid_ref_ids: invalidRefIds = [],
          },
        },
      } = await rosterIdentitiesCreate({
        variables: {
          input: {
            roster_identities: rosterIdentitiesInput,
            // Creates all identities that had no errors with them.
            skip_errors: true,
            claimed,
            grant_purchase: grantPurchase,
          },
        },
      });

      const hasInvalidItems =
        invalidNames.length > 0 ||
        invalidAddresses.length > 0 ||
        invalidPhones.length > 0 ||
        invalidRefIds.length > 0;

      // Gets reset regardless, since we create all valid identities anyway.
      setCreatedRosterIdentities([
        ...createdRosterIdentitiesCurrent,
        ...createdRosterIdentities,
      ]);

      if (!hasInvalidItems && invalidRosterIdentitiesPreCreate.length === 0) {
        // Reset, all is well.
        setSuccess(true);
        setInvalidRosterIdentities([]);
        setinvalidDataStore({
          claimed: false,
          grantPurchase: false,
          uniformConfiguration: undefined,
          contactLinkConfiguration: undefined,
        });
      } else {
        const invalidItemsCurrent = {
          // Addresses and names will generally speaking be empty, as we
          // validate them properly on the FE already, but here just in case.
          addresses: [...invalidAddresses, ...invalidItems.addresses],
          names: [...invalidNames, ...invalidItems.names],
          // FE validation is not enough for phones.
          // Remove the + by using parsePhone.
          phones: [
            ...invalidPhones.map(phone => parsePhone(phone)),
            ...invalidItems.phones,
          ],
          ref_ids: [...invalidRefIds, ...invalidItems.ref_ids],
        };

        // Invalidate failed RIs and set them as invalid identities.
        // Also update invalid items for future reference.
        const { invalidRosterIdentities: invalidRosterIdentitiesPostCreate } =
          validateRosterIdentities({
            rosterIdentities: validRosterIdentities,
            invalidItems: invalidItemsCurrent,
            importMode: uniformConfiguration
              ? ALBUM_FOLDER_MEMBERS_IMPORT_MODE.UNIFORM
              : ALBUM_FOLDER_MEMBERS_IMPORT_MODE.GENERAL,
            requiresAddress,
            ignoreContactInformationErrors,
            isYearbookEvent,
          });

        log.info(
          'onRosterIdentitiesCreate :: The following identities were found invalid after create: ',
          invalidRosterIdentitiesPostCreate,
        );
        log.info(
          'onRosterIdentitiesCreate :: Current invalid items: ',
          invalidItemsCurrent,
        );

        // Set invalid items including pre-and-post create roste ids
        const newInvalidRosterIdentities = uniqBy(
          [
            ...invalidRosterIdentitiesPostCreate,
            ...invalidRosterIdentitiesPreCreate,
          ],
          'rosterIdentity.id',
        );

        setInvalidRosterIdentities(newInvalidRosterIdentities);
        if (newInvalidRosterIdentities.length > 0) {
          setinvalidDataStore({
            claimed,
            grantPurchase,
            uniformConfiguration,
            contactLinkConfiguration,
          });
        }
        setInvalidItems(invalidItemsCurrent);
      }
    } catch (error) {
      setErrors([getErrorString(error)]);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    errors,
    success,
    sportInformationMissingWarning,
    invalidRosterIdentities,
    invalidDataStore,
    createdRosterIdentities,
    validateRosterIdentities: validateInput =>
      validateRosterIdentities({
        invalidItems,
        importMode: invalidDataStore?.uniformConfiguration
          ? ALBUM_FOLDER_MEMBERS_IMPORT_MODE.UNIFORM
          : ALBUM_FOLDER_MEMBERS_IMPORT_MODE.GENERAL,
        isYearbookEvent,
        ...validateInput,
      }),
    onRosterIdentitiesCreate,
    onRosterIdentitiesCreateFromColumnMatchedData: async (
      data,
      claimed = false,
      grantPurchase = false,
      uniformConfiguration = undefined,
      contactLinkConfiguration = undefined,
      requiresAddress = false,
      ignoreContactInformationErrors = false,
    ) => {
      // Fully reset the state:
      setInvalidRosterIdentities([]);
      setinvalidDataStore({
        claimed: false,
        grantPurchase: false,
        uniformConfiguration: undefined,
        contactLinkConfiguration: undefined,
      });
      const {
        rosterIdentities,
        maximumRefIdCopiesExceeder,
        missingSportData,
        missingGenderSportCombinations,
        invalidUrls,
      } = prepareColumnData(data);

      if (invalidUrls && invalidUrls.length > 0) {
        setErrors(invalidUrls);
        return null;
      }

      if (missingSportData) {
        setErrors([
          'Some rows with team, level, or jersey data were missing sport information. Please ensure that all rows with team, level, or jersey data have the Sport column populated.',
        ]);
        return null;
      }

      if (maximumRefIdCopiesExceeder) {
        setErrors([
          `The ID value of ${maximumRefIdCopiesExceeder} appears more than ${MAX_REF_ID_MERGE_CLONES} times on separate rows. Please make sure the data is correct and ensure the use of unique IDs for each row in your CSV file.`,
        ]);

        return null;
      }

      // If there are missing sport combinations, generate them
      let newSportCombinations = new Map();
      if (
        missingGenderSportCombinations &&
        missingGenderSportCombinations.length > 0
      ) {
        try {
          setLoading(true);
          newSportCombinations = await generateSportCombinations(
            missingGenderSportCombinations,
          );

          // Check if any combinations could not be generated (sport tag doesn't exist)
          const failedCombinations = missingGenderSportCombinations.filter(
            combo => {
              const combinationKey = `${(combo.sport ?? '').toLowerCase()}-${(
                combo.gender ?? ''
              ).toLowerCase()}-${(combo.level ?? '').toLowerCase()}-${(
                combo.team ?? ''
              ).toLowerCase()}`;
              return !newSportCombinations.has(combinationKey);
            },
          );

          if (failedCombinations.length > 0) {
            // Some combinations could not be generated because the sport doesn't exist
            const failedLabels = failedCombinations.map(combo => {
              const genderLabel = combo.gender
                ? `(${
                    combo.gender === ALBUM_FOLDER_TAG_GENDER.MALE
                      ? 'Boys'
                      : 'Girls'
                  }) `
                : '';
              const teamLabel = combo.team ? ` - Team: ${combo.team}` : '';
              return `${genderLabel}${combo.sport} - Level: ${combo.level}${teamLabel}`;
            });

            setErrors([
              <Fragment>
                The following sport combination(s) could not be generated
                because the sport is not configured in your event:
                {failedLabels.join(', ')}.<br />
                <br />
                Go to{' '}
                <Button
                  btnStyle="link"
                  btnSize="xs"
                  onClick={() => {
                    history.push(
                      `/app/${basename}/${
                        albumFolder.picture_day_enabled
                          ? 'picture-day/folders'
                          : 'folders'
                      }/${albumFolder.id}/settings/tags/smart`,
                    );
                    closeModal(ALERT_MODAL);
                  }}
                >
                  Gallery Settings &rarr; Tags
                </Button>{' '}
                to configure the sports for your event.
              </Fragment>,
            ]);
            setLoading(false);
            return null;
          }
        } catch (error) {
          setErrors([getErrorString(error)]);
          setLoading(false);
          return null;
        }
        setLoading(false);
      }

      // Apply generated combinations to roster identities with pending sport info
      const processedRosterIdentities = rosterIdentities.map(rosterIdentity => {
        if (!rosterIdentity.pendingSportInfo) {
          return rosterIdentity;
        }

        const { sport, gender, level, team } = rosterIdentity.pendingSportInfo;
        const combinationKey = `${(sport ?? '').toLowerCase()}-${(
          gender ?? ''
        ).toLowerCase()}-${(level ?? '').toLowerCase()}-${(
          team ?? ''
        ).toLowerCase()}`;
        const generatedConfig = newSportCombinations.get(combinationKey);

        if (generatedConfig) {
          // Add the generated tag to this roster identity
          return {
            ...rosterIdentity,
            tags: [
              ...rosterIdentity.tags,
              {
                ...omit(generatedConfig, [
                  'id',
                  'gender',
                  'sport',
                  'level',
                  'team',
                  'label',
                ]),
                jersey_number: rosterIdentity.jersey_number,
              },
            ],
            pendingSportInfo: undefined,
          };
        }

        // Remove the pending info even if we couldn't find the config
        return {
          ...rosterIdentity,
          pendingSportInfo: undefined,
        };
      });

      const result = await onRosterIdentitiesCreate(
        processedRosterIdentities,
        claimed,
        grantPurchase,
        uniformConfiguration,
        contactLinkConfiguration,
        requiresAddress,
        ignoreContactInformationErrors,
      );

      return result;
    },
  };
};

export default useRosterIdentitiesCreate;
